import React from "react";
import { Badge, Button, Callout, Card, DataTable, Dialog, Field, Input, PageHeader, Select } from "../components/DesignSystem";
import type { GiftReceived, GiftReceivedType } from "../domain/types";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";
import { ErrorState, LoadingState, useIdempotencyKey, useSubmitGuard } from "./common";

export function GiftsReceivedScreen() {
  const { workspace, loading, error, service, session } = usePlannerData();
  const canView = session?.user.role === "owner" || session?.user.role === "partner";
  const [gifts, setGifts] = React.useState<GiftReceived[] | null>(null);
  const [giftsLoading, setGiftsLoading] = React.useState(false);
  const [giftsError, setGiftsError] = React.useState<string | null>(null);
  const [adding, setAdding] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setGiftsLoading(true);
    setGiftsError(null);
    try {
      setGifts(await service.giftsReceived.list());
    } catch (err) {
      setGiftsError(errorMessage(err));
    } finally {
      setGiftsLoading(false);
    }
  }, [service]);

  React.useEffect(() => { if (canView) void load(); }, [load, canView]);

  if (loading || !workspace) return <div className="pob-pad"><LoadingState label="Loading gifts received" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;

  if (!canView) {
    return (
      <div>
        <PageHeader eyebrow="More" title="Gifts received" />
        <div className="pob-pad">
          <Callout tone="neutral" title="Owner and Partner only">
            Gifts received is kept private to the Owner and Partner — it never appears in exports, share links, or other collaborators' views.
          </Callout>
        </div>
      </div>
    );
  }

  const toggleThankYou = async (gift: GiftReceived) => {
    setActionError(null);
    try { await service.giftsReceived.updateGift(gift.id, { thankYouStatus: gift.thankYouStatus === "sent" ? "not_sent" : "sent" }); await load(); } catch (err) { setActionError(errorMessage(err)); }
  };
  const remove = async (gift: GiftReceived) => {
    setActionError(null);
    try { await service.giftsReceived.deleteGift(gift.id); await load(); } catch (err) { setActionError(errorMessage(err)); }
  };

  return (
    <div>
      <PageHeader eyebrow="More" title="Gifts received" meta={[{ icon: "lock", label: "Owner & Partner only" }]} actions={<Button variant="primary" icon="plus" onClick={() => setAdding(true)}>Record gift</Button>} />
      <div className="pob-pad">
        {giftsLoading && !gifts && <LoadingState label="Loading gifts received" />}
        {giftsError && <ErrorState message={giftsError} retry={load} />}
        {actionError && <Callout tone="error" title="That didn't work" style={{ marginBottom: "var(--space-4)" }}>{actionError}</Callout>}
        {gifts && (
          <Card padding="0">
            <DataTable
              rows={gifts}
              emptyLabel="No gifts recorded yet"
              columns={[
                { key: "giverName", header: "From" },
                { key: "householdName", header: "Household", muted: true, render: (row) => row.householdName || "Unassigned" },
                { key: "type", header: "Type", width: 100, render: (row) => <Badge tone="neutral">{row.type}</Badge> },
                { key: "description", header: "Description", muted: true },
                { key: "amount", header: "Amount", width: 110, render: (row) => row.amount != null ? `${row.currency || ""} ${row.amount.toLocaleString()}` : "—" },
                { key: "thankYouStatus", header: "Thank you", width: 120, render: (row) => <Button size="sm" variant={row.thankYouStatus === "sent" ? "primary" : "secondary"} onClick={() => toggleThankYou(row)}>{row.thankYouStatus === "sent" ? "Sent" : "Not sent"}</Button> },
                { key: "actions", header: "", width: 90, render: (row) => <Button size="sm" variant="danger" onClick={() => remove(row)}>Remove</Button> }
              ]}
            />
          </Card>
        )}
      </div>
      {adding && <AddGiftDialog onClose={() => setAdding(false)} onSaved={async () => { setAdding(false); await load(); }} />}
    </div>
  );
}

function AddGiftDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => Promise<void> }) {
  const { service, workspace } = usePlannerData();
  const [giverName, setGiverName] = React.useState("");
  const [householdId, setHouseholdId] = React.useState("");
  const [type, setType] = React.useState<GiftReceivedType>("item");
  const [description, setDescription] = React.useState("");
  const [amount, setAmount] = React.useState("");
  const [currency, setCurrency] = React.useState("PKR");
  const [formError, setFormError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();
  const idempotencyKey = useIdempotencyKey();

  const submit = () => guardedSubmit(async () => {
    setFormError(null);
    if (!giverName.trim()) { setFormError("Enter who the gift is from."); return; }
    try {
      await service.giftsReceived.createGift({ giverName: giverName.trim(), householdId: householdId || undefined, type, description: description || undefined, amount: amount ? Number(amount) : undefined, currency: type === "cash" ? currency : undefined }, idempotencyKey);
      await onSaved();
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog title="Record a gift" onClose={onClose} footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" onClick={submit} disabled={submitting}>{submitting ? "Saving…" : "Save"}</Button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {formError && <Callout tone="error" title="Check the form">{formError}</Callout>}
        <Field label="From"><Input value={giverName} onChange={(e) => setGiverName(e.target.value)} placeholder="Name on the card, or leave general" /></Field>
        <Field label="Household (optional — leave unassigned if unclear)"><Select value={householdId} placeholder="Unassigned" onChange={(e) => setHouseholdId(e.target.value)} options={workspace!.households.map((h) => ({ value: h.id, label: h.name }))} /></Field>
        <Field label="Type"><Select value={type} onChange={(e) => setType(e.target.value as GiftReceivedType)} options={[{ value: "item", label: "Item" }, { value: "cash", label: "Cash" }, { value: "contribution", label: "Contribution" }]} /></Field>
        {type === "cash" && (
          <div className="pob-grid-2">
            <Field label="Amount"><Input value={amount} type="number" onChange={(e) => setAmount(e.target.value)} /></Field>
            <Field label="Currency"><Input value={currency} onChange={(e) => setCurrency(e.target.value)} /></Field>
          </div>
        )}
        <Field label="Description"><Input value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
      </div>
    </Dialog>
  );
}
